import Drawer from "react-modern-drawer";
import { IoClose } from "react-icons/io5";
import { IoArrowForward } from "react-icons/io5";
import DOC_STYLES from "../../../../Sales/utils/docStyles";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../../../../context/settings";

const PRIMARY = "#6C63FF";

const TYPE_COLORS = {
  purchase:   { bg: "#e8f5e9", color: "#2e7d32", labelKey: "typePurchase" },
  sale:       { bg: "#fce4ec", color: "#c62828", labelKey: "typeSale" },
  adjustment: { bg: "#fff3e0", color: "#e65100", labelKey: "typeAdjustment" },
  transfer:   { bg: "#e3f2fd", color: "#1565c0", labelKey: "typeTransfer" },
  return:     { bg: "#f3e5f5", color: "#6a1b9a", labelKey: "typeReturn" },
};

function TypeBadge({ type }) {
  const { t: translate } = useTranslation();
  const lower = type?.toLowerCase();
  const config = TYPE_COLORS[lower];
  const style = config ?? { bg: "#f0f0f0", color: "#555" };
  const label = config ? translate(`inventory.${config.labelKey}`) : type;
  return (
    <span style={{
      display: "inline-block",
      background: style.bg,
      color: style.color,
      borderRadius: 6,
      padding: "3px 10px",
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 0.3,
      textTransform: "capitalize",
    }}>
      {label}
    </span>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={DOC_STYLES.label}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: "#1d1d1f" }}>{value ?? "—"}</span>
    </div>
  );
}

export default function ViewDrawer({ movement, isOpen, onClose }) {
  const { t } = useTranslation();
  const { formatDateTime } = useSettings();
  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      direction="right"
      size={520}
      enableOverlay
      lockScroll
      zIndex={9000}
      duration={200}
    >
      <div style={DOC_STYLES.wrap}>

        {/* Header */}
        <div style={DOC_STYLES.header}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              style={{ background: "none", border: "none", cursor: "pointer", color: "#888", padding: 4, borderRadius: 6, display: "flex", alignItems: "center" }}
              onClick={onClose}
              onMouseEnter={e => { e.currentTarget.style.background = "#f0f0f0"; e.currentTarget.style.color = "#333"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "#888"; }}
            >
              <IoClose size={20} />
            </button>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#1d1d1f" }}>{t('inventory.movementDetails')}</span>
          </div>
          {movement && <TypeBadge type={movement.type} />}
        </div>

        {/* Scrollable content */}
        <div style={DOC_STYLES.scroll}>
          {movement && (
            <div style={{ padding: "28px 32px", display: "flex", flexDirection: "column", gap: 28 }}>

              {/* Product */}
              <div style={{ background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: 10, padding: "20px 24px" }}>
                <div style={DOC_STYLES.label}>{t('inventory.product')}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#1d1d1f", marginTop: 4 }}>
                  {movement.product?.name ?? "—"}
                </div>
              </div>

              {/* Stock change visualization */}
              <div style={{ background: "linear-gradient(135deg, #f4f2ff 0%, #ede9ff 100%)", border: "1px solid #ddd6ff", borderRadius: 12, padding: "24px 28px" }}>
                <div style={DOC_STYLES.label}>{t('inventory.stockMovement')}</div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16 }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#9b93e0", letterSpacing: 0.8, marginBottom: 6 }}>{t('inventory.previousStockShort')}</div>
                    <div style={{ fontSize: 32, fontWeight: 800, color: PRIMARY }}>{movement.previous_stock ?? 0}</div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                    <IoArrowForward size={22} color={PRIMARY} />
                    <span style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: movement.quantity >= 0 ? "#2e7d32" : "#c62828",
                    }}>
                      {movement.quantity >= 0 ? "+" : ""}{movement.quantity}
                    </span>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#9b93e0", letterSpacing: 0.8, marginBottom: 6 }}>{t('inventory.newStockShort')}</div>
                    <div style={{ fontSize: 32, fontWeight: 800, color: PRIMARY }}>{movement.new_stock ?? 0}</div>
                  </div>
                </div>
              </div>

              {/* Meta info */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                <InfoRow label={t('common.date')} value={formatDateTime(movement.created_at)} />
                <InfoRow label={t('common.type')} value={<TypeBadge type={movement.type} />} />
                <InfoRow label={t('common.reference')} value={movement.reference || t('common.notAvailable')} />
              </div>

            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
}
